import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Truck, Save, Percent, AlertTriangle } from "lucide-react";
import { PickupCoverage } from "@/components/PickupCoverage";

const MK = [["admin_pct", "Ricarico admin %"], ["dealer_pct", "Quota dealer %"], ["agency_pct", "Quota agenzia %"], ["master_pct", "Quota master %"]];

function MarkupDefault({ mk, onChange }) {
  return (
    <Card className="p-5 glow-border space-y-3" data-testid="ship-mk-default">
      <h2 className="font-head font-semibold flex items-center gap-2"><Percent className="w-4 h-4 text-primary" /> Ricarico predefinito</h2>
      <div className="grid sm:grid-cols-4 gap-3">
        {MK.map(([k, l]) => <div key={k}><Label>{l}</Label><Input className="mt-1.5" type="number" step="0.5" value={mk[k]} onChange={(e) => onChange({ ...mk, [k]: e.target.value })} data-testid={`ship-mk-${k}`} /></div>)}
      </div>
      <p className="text-xs text-muted-foreground">Il ricarico si applica al nostro costo (base + carburante). Le quote di dealer, agenzia e master sono parte del ricarico e vanno sui wallet (agenzia: da fatturare); se un ruolo manca, la sua quota resta all'admin.</p>
    </Card>
  );
}

function ServiceCard({ s, i, weights, onChange }) {
  const set = (patch) => onChange(i, { ...s, ...patch });
  return (
    <Card className={`p-5 glow-border space-y-3 ${s.enabled ? "" : "opacity-70"}`} data-testid={`ship-svc-${s.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-head font-semibold">{s.name}</p>
          <div className="text-xs text-muted-foreground">{s.carrier_name} · <Badge variant="outline" className="text-[10px]">{s.type === "locker" ? "Locker" : s.type === "punto" ? "Punto di ritiro" : "Domicilio"}</Badge> · {s.eta}</div>
        </div>
        <label className="flex items-center gap-2 text-sm">{s.enabled ? "Attivo" : "Disattivo"}<Switch checked={!!s.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid={`ship-svc-enabled-${s.id}`} /></label>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Peso</TableHead><TableHead>Costo a noi (base)</TableHead><TableHead className="text-right">+ carburante</TableHead><TableHead className="text-right">Ricarico</TableHead><TableHead className="text-right">Dealer / Ag. / Master</TableHead><TableHead className="text-right">Netto admin</TableHead><TableHead className="text-right">Prezzo dealer (IVA incl.)</TableHead></TableRow></TableHeader>
          <TableBody>
            {weights.map((w) => {
              const p = s.prices?.[w.key];
              return (
                <TableRow key={w.key}>
                  <TableCell className="text-sm">{w.label}</TableCell>
                  <TableCell><Input type="number" step="0.01" className="h-8 w-24" value={s.costs?.[w.key] ?? ""} onChange={(e) => set({ costs: { ...s.costs, [w.key]: e.target.value } })} data-testid={`ship-cost-${s.id}-${w.key}`} /></TableCell>
                  <TableCell className="text-right text-sm">{p ? euro(p.cost) : "—"}</TableCell>
                  <TableCell className="text-right text-sm">{p ? euro(p.markup) : "—"}</TableCell>
                  <TableCell className="text-right text-xs">{p ? `${euro(p.shares.dealer)} / ${euro(p.shares.agency)} / ${euro(p.shares.master)}` : "—"}</TableCell>
                  <TableCell className="text-right text-sm">{p ? euro(p.admin_net) : "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{p ? euro(p.total) : "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        {MK.map(([k, l]) => <div key={k}><Label className="text-xs">{l} (vuoto = predefinito)</Label><Input className="mt-1 h-8" type="number" step="0.5" value={s.markup?.[k] ?? ""} onChange={(e) => set({ markup: { ...s.markup, [k]: e.target.value } })} data-testid={`ship-svc-mk-${s.id}-${k}`} /></div>)}
      </div>
    </Card>
  );
}

function VedilooRates({ weights }) {
  const [w, setW] = useState("1");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/ship/vediloo-rates", { params: { weight_key: w } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [w]);
  return (
    <Card className="p-5 glow-border space-y-3" data-testid="ship-vediloo-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-head font-semibold">Servizi disponibili nel contratto Vediloo</h2>
        <select className="bg-background border border-border rounded-md h-9 px-2 text-sm" value={w} onChange={(e) => setW(e.target.value)} data-testid="ship-vediloo-weight">
          {weights.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
      </div>
      {!d && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {d && !d.configured && <p className="text-sm text-amber-500" data-testid="ship-vediloo-off">Vediloo non configurato in questo ambiente (Integrazioni): uso il listino manuale.</p>}
      {d && d.configured && (d.rates || []).length === 0 && <p className="text-sm text-amber-500" data-testid="ship-vediloo-empty">{d.provider_error || "Vediloo non ha restituito tariffe."} Finché non arrivano uso il listino manuale qui sotto.</p>}
      {(d?.rates || []).map((r) => {
        const svc = Object.entries(d.matched || {}).find(([, v]) => v === r.vid)?.[0];
        return (
          <div key={r.vid} className="flex items-center justify-between text-sm border-b border-border py-1.5" data-testid={`ship-vediloo-rate-${r.vid}`}>
            <span>{r.name} <span className="text-muted-foreground">· {r.carrier} · {r.kind}</span></span>
            <span className="flex items-center gap-2">{svc ? <Badge variant="outline" className="text-teal-500">usato per {svc}</Badge> : <Badge variant="outline">non collegato</Badge>}<strong>{euro(r.price)}</strong></span>
          </div>
        );
      })}
      <p className="text-xs text-muted-foreground">Quando un servizio è collegato, il costo a noi arriva da Vediloo (carburante incluso) e il ricarico si applica su quello; altrimenti si usa il listino manuale qui sotto.</p>
    </Card>
  );
}

export default function AdminShipServices() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (r) => setD(r.data);
  useEffect(() => { api.get("/admin/ship/services").then(load).catch(() => {}); }, []);
  if (!d) return null;
  const change = (i, s) => setD({ ...d, services: d.services.map((x, j) => (j === i ? s : x)) });
  const save = async () => {
    setBusy(true);
    try { load(await api.put("/admin/ship/services", { services: d.services.map(({ prices, ...s }) => s), markup_default: d.markup_default })); toast.success("Servizi salvati: prezzi ricalcolati"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="ship-services-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight flex items-center gap-2"><Truck className="w-7 h-7 text-primary" /> Servizi di spedizione</h1>
          <p className="text-sm text-muted-foreground mt-1">Tutte le combinazioni corriere × tipo di consegna, con costi a noi, ricarichi e prezzo finale al dealer. Carburante attuale {(d.fuel_pct * 100).toFixed(1)}% (Config Spedizioni).</p>
        </div>
        <Button onClick={save} disabled={busy} className="gap-2" data-testid="ship-services-save"><Save className="w-4 h-4" /> Salva tutto</Button>
      </div>
      <p className="text-xs text-amber-500 flex items-center gap-1.5" data-testid="ship-costs-warning"><AlertTriangle className="w-3.5 h-3.5" /> I costi base sono un listino di partenza da verificare: inserisci quelli del tuo contratto Vediloo (o verranno letti in automatico quando il collegamento sarà attivo).</p>
      <MarkupDefault mk={d.markup_default} onChange={(mk) => setD({ ...d, markup_default: mk })} />
      <VedilooRates weights={d.weights} />
      <PickupCoverage />
      {d.services.map((s, i) => <ServiceCard key={s.id} s={s} i={i} weights={d.weights} onChange={change} />)}
    </div>
  );
}
